import { useLocation } from 'wouter'
import {
  ArrowUpRight,
  Compass,
  Headset,
  HelpCircle,
  LifeBuoy,
  MessageSquarePlus,
  PlayCircle,
  ScrollText,
} from '@/components/ds/icons'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tip } from '@/components/ds/Tip'
import { useActiveGuide } from '@/contexts/ActiveGuideContext'
import { whatsappSoporte } from '@/lib/support'
import { TUTORIALES_ROUTE } from '@/lib/tutoriales'

/** Ruta de la página de novedades / registro de cambios (feed servido por API). */
const CHANGELOG_ROUTE = '/novedades'

/**
 * Botón flotante fijo (abajo a la derecha) que agrupa los accesos de ayuda y
 * recursos en un único menú. Reemplaza al icono "?" por página que reactivaba
 * la guía: ahora "Ver Guía" usa la guía de la ruta actual vía ActiveGuideContext.
 *
 * Se monta una sola vez en el layout autenticado (App.tsx), dentro de
 * ActiveGuideProvider, por lo que aparece en todas las pantallas del tenant y
 * no en login / legales / onboarding.
 */
export function HelpResourcesFab() {
  const [, navigate] = useLocation()
  const { activeGuideStart } = useActiveGuide()

  const verGuia = () => {
    // Diferimos un tick para que el menú termine de cerrarse y devolver el foco
    // antes de que driver.js monte su overlay, evitando conflictos de focus-trap.
    const start = activeGuideStart
    if (start) setTimeout(start, 0)
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 print:hidden">
      <DropdownMenu>
        <Tip texto="Ayuda y Recursos" lado="left">
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              aria-label="Ayuda y Recursos"
              className="h-12 w-12 rounded-full shadow-lg"
            >
              <HelpCircle className="h-6 w-6" />
            </Button>
          </DropdownMenuTrigger>
        </Tip>

        <DropdownMenuContent side="top" align="end" sideOffset={12} className="w-60">
          <DropdownMenuGroup>
            <DropdownMenuItem disabled>
              <LifeBuoy className="mr-2 h-4 w-4" />
              Centro de Ayuda
              <span className="ml-auto text-xs text-muted-foreground">Pronto</span>
            </DropdownMenuItem>
            {/*
              Tutoriales: se abre en una pestaña nueva a propósito. El uso real
              es mirar el video y probar en el panel al mismo tiempo; navegar en
              la misma pestaña obliga a ir y volver, y encima pierde la pantalla
              donde el usuario estaba.

              Tiene que ser un <a> plano: <Link> de wouter ignora `target` y
              navega igual en la misma pestaña. La página de tutoriales es una
              ruta pública, así que la pestaña nueva —sin sesión, porque el token
              vive en sessionStorage— la renderiza sin problema.
            */}
            <DropdownMenuItem asChild>
              <a href={TUTORIALES_ROUTE} target="_blank" rel="noopener noreferrer">
                <PlayCircle className="mr-2 h-4 w-4" />
                Tutoriales
                <ArrowUpRight className="text-soft ml-auto h-3.5 w-3.5" />
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate(CHANGELOG_ROUTE)}>
              <ScrollText className="mr-2 h-4 w-4" />
              Change Logs
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/*
            Feedback y Soporte salen por WhatsApp. Van en pestaña nueva y con la
            flechita de "esto se va afuera", igual que Tutoriales: antes eran
            `mailto:` y abrían el cliente de correo sin mover el panel, pero
            `wa.me` navega de verdad y se llevaría puesta la pantalla actual.
          */}
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <a
                href={whatsappSoporte('Hola, quiero enviarles un comentario sobre Fácil Control.')}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageSquarePlus className="mr-2 h-4 w-4" />
                Enviar Feedback
                <ArrowUpRight className="text-soft ml-auto h-3.5 w-3.5" />
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a
                href={whatsappSoporte('Hola, necesito ayuda con Fácil Control.')}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Headset className="mr-2 h-4 w-4" />
                Contactar con Soporte
                <ArrowUpRight className="text-soft ml-auto h-3.5 w-3.5" />
              </a>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuItem disabled={!activeGuideStart} onSelect={verGuia}>
            <Compass className="mr-2 h-4 w-4" />
            Ver Guía
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
